import {
  FunnelCreateTypes,
  FunnelCounts,
  FunnelMetricsDataType,
  FunnelsRequestTypes,
  FunnelMetricsRequestBody,
  ComputeFunnelMetricsResponseWithId,
} from './types';
import { createAction, ActionType } from '@fullstory/redux-util/utils/types';
import { Funnel } from '@fullstory/proto/fs/services/search-settings';
import { TimeInterval } from '@fullstory/proto/fs/api/segment';
import {
  createGRPCAction,
  createRequestCancellationAction,
  RequestActionType,
  RequestDispatch,
} from '@fullstory/redux-util';
import { SearchCacheKey } from '../searchMeta/types';
import { Flags } from '@fullstory/client-api';
import jstz from 'jstz';
import { UID } from '@fullstory/util/uid';
import {
  eventStateToEventType,
  funnelMetricsRequestBodyToComputeFunnelMetricsRequest,
  buildRevOppsMetricsRequest,
  isRevenueFunnel,
  funnelsCacheOptionsRefresh,
  funnelsCacheOptionsRefreshMinute,
} from './util';
import { hashObject } from '@fullstory/util/hash';
import {
  ComputeFunnelMetricsRequest,
  FunnelMetricsServicePublic,
  QueryCacheOptions,
  ComputeFunnelMetricsResponse,
  RequestAnalyticsAttributes,
} from '@fullstory/proto/fs/services/alexa';
import { NumericAggregationMethod } from '@fullstory/proto/fs/api/metrics';
import { FunnelDataTable } from '@fullstory/proto/fs/api/metrics/funnelmetrics';
import { BatchOptions } from '@fullstory/util/xhr';
import { CanAccessRequest } from '@fullstory/proto/fs/shared/accesscontrol';
import { int64, Timestamp, timestamp } from '@fullstory/proto/google/protobuf';
import { isDataFresh } from '@fullstory/searchiecache';
import { isEqual } from 'lodash';
import { AppAction, AppDispatch } from '../../types';
import { computeRevenueMetricRequest } from '../dashboards/actions';
import { createAppCacheActions } from '@fullstory/redux-util/utils';
import { createAppCacheRequestAction } from '@fullstory/redux-util/utils/appCache/actions';
import { RevenueEvent } from '@fullstory/proto/fs/services/webber/conversions';
import * as dxo from '@fullstory/proto/fs/services/dx-objects';
import { funnelCacheKeysToInvalidate } from '../../utils/funnels';

// Actions for creation modal
export const FunnelCreateActions = {
  showModal: (show: boolean, isUpdate?: boolean) =>
    createAction({
      type: FunnelCreateTypes.ShowModal,
      show,
      isUpdate,
    }),
  resetModal: () =>
    createAction({
      type: FunnelCreateTypes.ResetModal,
    }),
  setSelected: (funnel: Funnel) =>
    createAction({
      type: FunnelCreateTypes.SetSelected,
      funnel,
    }),
  setFunnelData: (data: FunnelCounts[]) =>
    createAction({
      type: FunnelCreateTypes.SetFunnelData,
      data,
    }),
  fetchFunnelDataFailed: () =>
    createAction({
      type: FunnelCreateTypes.FetchFunnelDataFailed,
    }),
};

export type FunnelCreateActions = ActionType<typeof FunnelCreateActions>;

export const FunnelCreateAsyncActions = {
  fetchFunnelMetrics: (
    req: Omit<ComputeFunnelMetricsRequest, 'now' | 'timezone'>,
    searchCacheKey: SearchCacheKey,
    flags: Flags,
  ) => {
    const funnelCountRequest: ComputeFunnelMetricsRequest = {
      ...req,
      now: null, // current time is implied
      timezone: jstz.determine().name(),
    };
    return createGRPCAction<typeof FunnelMetricsServicePublic.ComputeFunnelMetrics, FunnelCounts[]>(
      {
        modify: (resp): FunnelCounts[] => {
          const funnelEventSequence = funnelCountRequest.computation?.funnel?.events;
          return resp.data && resp.data.steps
            ? resp.data.steps.map((c: FunnelDataTable, i: number) => ({
                count: c.table[0]?.value ?? 0,
                step: eventStateToEventType(funnelEventSequence?.events[i] || null, flags).primary,
              }))
            : [];
        },
        cacheKey: url => `${url}:${hashObject(funnelCountRequest)}:${hashObject(searchCacheKey)}`,
      },
    )(
      {
        type: FunnelCreateTypes.FetchFunnelData,
        grpcFunction: FunnelMetricsServicePublic.ComputeFunnelMetrics,
        body: funnelCountRequest,
        readWrite: 'write',
        settings: {
          headers: { 'GRPC-Timeout': '1M' }, // allow for 1 minute before context deadline exceeded
        },
      },
      FunnelCreateTypes.SetFunnelData,
      FunnelCreateTypes.FetchFunnelDataFailed,
    );
  },
};

export const FunnelsRequestActions = {
  getFunnel: (orgId: string, funnelId: string, force?: boolean) =>
    createGRPCAction<typeof dxo.FunnelsPublic.GetFunnel>()(
      {
        type: FunnelsRequestTypes.GetFunnelRequest,
        grpcFunction: dxo.FunnelsPublic.GetFunnel,
        body: { orgId, funnelId, version: int64.fromNumber(0), limit: 1 },
        readWrite: 'read',
        force,
      },
      FunnelsRequestTypes.GetFunnelSuccess,
      FunnelsRequestTypes.GetFunnelFailure,
    ),
  getAllFunnels: (orgId: string, force?: boolean, includeArchived = false) =>
    createGRPCAction<typeof dxo.FunnelsPublic.GetAllFunnels>()(
      {
        type: FunnelsRequestTypes.GetAllFunnelsRequest,
        grpcFunction: dxo.FunnelsPublic.GetAllFunnels,
        body: { orgId, includeArchived },
        readWrite: 'read',
        force,
      },
      FunnelsRequestTypes.GetAllFunnelsSuccess,
      FunnelsRequestTypes.GetAllFunnelsFailure,
    ),
  setFunnel: (body: dxo.SetFunnelRequest) =>
    createGRPCAction<typeof dxo.FunnelsPublic.SetFunnel>()(
      {
        type: FunnelsRequestTypes.SetFunnelRequest,
        grpcFunction: dxo.FunnelsPublic.SetFunnel,
        body,
        force: true,
        readWrite: 'write',
        invalidateCacheKey: funnelCacheKeysToInvalidate(body.orgId),
      },
      FunnelsRequestTypes.SetFunnelSuccess,
      FunnelsRequestTypes.SetFunnelFailure,
    ),
  markFunnelViewed: (body: dxo.MarkFunnelViewedRequest) =>
    createGRPCAction<typeof dxo.FunnelsPublic.MarkFunnelViewed, { lastViewed: timestamp }>({
      modify: () => ({ lastViewed: Timestamp.now() }),
    })(
      {
        type: FunnelsRequestTypes.MarkViewedRequest,
        grpcFunction: dxo.FunnelsPublic.MarkFunnelViewed,
        body,
        force: true,
        readWrite: 'write',
      },
      FunnelsRequestTypes.MarkViewedSuccess,
      FunnelsRequestTypes.MarkViewedFailure,
    ),
  starFunnel: (body: dxo.StarFunnelRequest) =>
    createGRPCAction<typeof dxo.FunnelsPublic.StarFunnel>()(
      {
        type: FunnelsRequestTypes.StarFunnelRequest,
        grpcFunction: dxo.FunnelsPublic.StarFunnel,
        body,
        force: true,
        readWrite: 'write',
        invalidateCacheKey: funnelCacheKeysToInvalidate(body.orgId),
      },
      FunnelsRequestTypes.StarFunnelSuccess,
      FunnelsRequestTypes.StarFunnelFailure,
    ),
  archiveFunnel: (body: dxo.ArchiveFunnelRequest) =>
    createGRPCAction<typeof dxo.FunnelsPublic.ArchiveFunnel>()(
      {
        type: FunnelsRequestTypes.ArchiveFunnelRequest,
        grpcFunction: dxo.FunnelsPublic.ArchiveFunnel,
        body,
        force: true,
        readWrite: 'write',
        invalidateCacheKey: funnelCacheKeysToInvalidate(body.orgId),
      },
      FunnelsRequestTypes.ArchiveFunnelSuccess,
      FunnelsRequestTypes.ArchiveFunnelFailure,
    ),
  computeFunnelMetrics: (
    req: FunnelMetricsRequestBody,
    searchCacheKey: SearchCacheKey,
    batchOptions?: BatchOptions,
    force = false,
    uid?: UID,
  ) => {
    const funnelMetricsRequest = funnelMetricsRequestBodyToComputeFunnelMetricsRequest(req);
    return createGRPCAction<
      typeof FunnelMetricsServicePublic.ComputeFunnelMetrics,
      ComputeFunnelMetricsResponseWithId
    >({
      // this gives funnel metrics unique and stable IDs that are used as keys,
      // necessary for correct funnel chart display
      modify: (rsp: ComputeFunnelMetricsResponse) =>
        rsp.data
          ? {
              ...rsp,
              data: {
                steps: rsp.data.steps.map(step => ({
                  ...step,
                  id: UID(),
                  table: step.table.map(funnelDataRow => ({ ...funnelDataRow, id: UID() })),
                })),
              },
            }
          : { ...rsp, data: null },
      cacheKey: (url: string) => {
        const { requestAnalytics, cacheOptions, ...requestCacheData } = funnelMetricsRequest;
        return `${url}:${hashObject(requestCacheData)}:${hashObject(searchCacheKey)}`;
      },
      uid,
    })(
      {
        type: FunnelsRequestTypes.ComputeFunnelMetricsRequest,
        grpcFunction: FunnelMetricsServicePublic.ComputeFunnelMetrics,
        body: funnelMetricsRequest,
        readWrite: 'write',
        settings: {
          headers: {
            'GRPC-Timeout': '1M', // allow for 1 minute before context deadline exceeded
            'X-css-as-elements': 'true',
          },
          batchOptions,
        },
        force,
      },
      FunnelsRequestTypes.ComputeFunnelMetricsSuccess,
      FunnelsRequestTypes.ComputeFunnelMetricsFailure,
    );
  },
  createUnnamedFunnel: (body: dxo.CreateUnnamedFunnelRequest) =>
    createGRPCAction<typeof dxo.FunnelsPublic.CreateUnnamedFunnel>()(
      {
        type: FunnelsRequestTypes.CreateUnnamedFunnelRequest,
        grpcFunction: dxo.FunnelsPublic.CreateUnnamedFunnel,
        body,
        readWrite: 'write',
      },
      FunnelsRequestTypes.CreateUnnamedFunnelSuccess,
      FunnelsRequestTypes.CreateUnnamedFunnelFailure,
    ),
  getUnnamedFunnel: (body: dxo.GetUnnamedFunnelRequest) =>
    createGRPCAction<typeof dxo.FunnelsPublic.GetUnnamedFunnel>()(
      {
        type: FunnelsRequestTypes.GetUnnamedFunnelRequest,
        grpcFunction: dxo.FunnelsPublic.GetUnnamedFunnel,
        body,
        readWrite: 'read',
      },
      FunnelsRequestTypes.GetUnnamedFunnelSuccess,
      FunnelsRequestTypes.GetUnnamedFunnelFailure,
    ),
  getAccess: (body: CanAccessRequest) =>
    createGRPCAction<typeof dxo.FunnelsPublic.GetFunnelsAccess>()(
      {
        type: FunnelsRequestTypes.GetFunnelsAccessRequest,
        grpcFunction: dxo.FunnelsPublic.GetFunnelsAccess,
        body,
        readWrite: 'read',
      },
      FunnelsRequestTypes.GetFunnelsAccessSuccess,
      FunnelsRequestTypes.GetFunnelsAccessFailure,
    ),
};

export const FunnelMetricActions = createAppCacheActions(
  FunnelsRequestTypes.FunnelAppCacheIsLoading,
  FunnelsRequestTypes.FunnelAppCacheLoadFailed,
  FunnelsRequestTypes.FunnelAppCacheRefetching,
  FunnelsRequestTypes.FunnelAppCacheHasData,
  FunnelsRequestTypes.FunnelAppCacheFresh,
  FunnelsRequestTypes.FunnelAppCacheSwap,
  FunnelsRequestTypes.FunnelAppCacheClear,
)<FunnelMetricsDataType>();

export type FunnelsActions = RequestActionType<typeof FunnelsRequestActions>;
export type FunnelMetricActions = ActionType<typeof FunnelMetricActions>;

export interface FunnelsPayload {
  body: FunnelMetricsRequestBody;
  searchCacheKey: SearchCacheKey;
  flags: Flags;
  cacheOptions: QueryCacheOptions;
}

export const FunnelsAsyncActions = {
  appCacheFunnelMetrics: (
    revenueEvent: RevenueEvent | null,
    timeInterval: TimeInterval,
    requestAnalytics?: RequestAnalyticsAttributes,
    batchOptions?: BatchOptions,
    uid?: [UID, UID, UID],
  ) => ({
    action: createAppCacheRequestAction<FunnelMetricsDataType, FunnelsPayload>()({
      query: (
        dispatch: RequestDispatch<AppAction>,
        payload: FunnelsPayload,
        isRefresh: boolean,
      ): Promise<FunnelMetricsDataType> => {
        const cacheOptions = isRefresh
          ? timeInterval === TimeInterval.TIME_INTERVAL_MINUTE
            ? funnelsCacheOptionsRefreshMinute
            : funnelsCacheOptionsRefresh
          : payload.cacheOptions;
        const ids = isRefresh ? undefined : uid;
        const isRevFunnel = isRevenueFunnel(payload.body.computation?.funnel?.events, revenueEvent);
        const funnelMetricPromise = dispatch(
          FunnelsRequestActions.computeFunnelMetrics(
            {
              ...payload.body,
              cacheOptions,
            },
            payload.searchCacheKey,
            batchOptions,
            isRefresh, // if user specifically requested a refresh, force the request action
            ids?.[0],
          ),
        );
        let totalRevenuePromise: Promise<undefined> | Promise<number> = Promise.resolve(undefined);
        let averageOrderValuePromise: Promise<undefined> | Promise<number> =
          Promise.resolve(undefined);
        if (isRevFunnel) {
          totalRevenuePromise = dispatch(
            computeRevenueMetricRequest(
              {
                orgId: payload.body.orgId,
                computation: buildRevOppsMetricsRequest(
                  payload.body.computation,
                  revenueEvent,
                  NumericAggregationMethod.NUMERIC_AGGREGATION_METHOD_SUM,
                ),
                cacheOptions,
                sampleOptions: null,
                requestAnalytics: requestAnalytics ?? null,
                pieConsumptionData: null,
              },
              payload.searchCacheKey,
              ids?.[1],
            ),
          );
          averageOrderValuePromise = dispatch(
            computeRevenueMetricRequest(
              {
                orgId: payload.body.orgId,
                computation: buildRevOppsMetricsRequest(
                  payload.body.computation,
                  revenueEvent,
                  NumericAggregationMethod.NUMERIC_AGGREGATION_METHOD_AVERAGE,
                ),
                cacheOptions,
                sampleOptions: null,
                requestAnalytics: requestAnalytics ?? null,
                pieConsumptionData: null,
              },
              payload.searchCacheKey,
              ids?.[2],
            ),
          );
        }
        return Promise.all([
          funnelMetricPromise,
          totalRevenuePromise,
          averageOrderValuePromise,
        ]).then(([funnelMetrics, totalRevenue, averageOrderValue]) => ({
          funnelMetrics,
          totalRevenue,
          averageOrderValue,
        }));
      },
      loading: FunnelMetricActions.appCacheIsLoading,
      failed: FunnelMetricActions.appCacheLoadFailed,
      refetching: FunnelMetricActions.appCacheIsRefetching,
      hasData: FunnelMetricActions.appCacheHasData,
      fresh: FunnelMetricActions.appCacheFresh,
      isDataFresh: (payload: FunnelsPayload, data: FunnelMetricsDataType) =>
        isDataFresh(
          payload.cacheOptions,
          Timestamp.mustDate(data.funnelMetrics.computedAt),
          new Date(),
        ),
      isSimiliar: (newData: FunnelMetricsDataType, oldData: FunnelMetricsDataType) =>
        isEqual(newData.funnelMetrics.data, oldData.funnelMetrics.data),
    }),
    cancelSingle: (dispatch: AppDispatch, uids: [UID, UID, UID]) => {
      uids.forEach(u => dispatch(createRequestCancellationAction(u)));
    },
  }),
};
